import { useEffect, useMemo, useState } from 'react'
import { play } from '../../lib/feedback'

/**
 * The moment she finishes.
 *
 * Warm without being sugary: it names what she did rather than praising her,
 * because "you finished the whole thing" is true and repeatable while "you're
 * so clever" is neither. No score, no stars earned, nothing to compare against
 * — Kid Mode shows her no data about herself.
 *
 * The bloom is an aurora that opens once and settles. Under reduced motion the
 * same aurora is simply there, already open, with no particles — the moment
 * still lands, it just does not move.
 */

const PARTICLE_COUNT = 18

export function Celebration({
  title = 'You did it',
  detail,
  onAgain,
  onDone,
}: {
  title?: string
  detail?: string
  onAgain?: () => void
  onDone: () => void
}) {
  const [ready, setReady] = useState(false)

  // Evenly spread by construction rather than randomly, so the bloom is always
  // balanced instead of occasionally clumping to one side.
  const particles = useMemo(
    () =>
      Array.from({ length: PARTICLE_COUNT }, (_, i) => ({
        angle: (360 / PARTICLE_COUNT) * i + (i % 2 ? 6 : -6),
        distance: 34 + ((i * 37) % 26),
        delay: (i % 6) * 40,
        size: 6 + ((i * 13) % 7),
      })),
    [],
  )

  useEffect(() => {
    play('celebrate')
    // Buttons appear a beat after the bloom so a stray finger already on the
    // screen cannot dismiss the moment before she has seen it.
    const t = window.setTimeout(() => setReady(true), 900)
    return () => window.clearTimeout(t)
  }, [])

  return (
    <div className="celebrate" role="status" aria-live="polite">
      <div className="celebrate-bloom" aria-hidden="true">
        <span className="celebrate-core" />
        {particles.map((p, i) => (
          <span
            key={i}
            className="celebrate-particle"
            style={
              {
                '--angle': `${p.angle}deg`,
                '--distance': `${p.distance}vmin`,
                '--delay': `${p.delay}ms`,
                '--size': `${p.size}px`,
              } as React.CSSProperties
            }
          />
        ))}
      </div>

      <div className="celebrate-text">
        <p className="celebrate-title">{title}</p>
        {detail && <p className="celebrate-detail">{detail}</p>}
      </div>

      <div className={`celebrate-actions${ready ? ' is-ready' : ''}`}>
        {onAgain && (
          <button className="kid-btn" onClick={onAgain}>
            <ReplayIcon />
            Again
          </button>
        )}
        <button className="kid-btn kid-btn-quiet" onClick={onDone}>
          <DoneIcon />
          All done
        </button>
      </div>
    </div>
  )
}

const S = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
}

const ReplayIcon = () => (
  <svg {...S} width="30" height="30">
    <path d="M3 12a9 9 0 1 0 3-6.7" />
    <path d="M3 4v5h5" />
  </svg>
)

const DoneIcon = () => (
  <svg {...S} width="30" height="30">
    <path d="M4 12.5l5 5L20 6.5" />
  </svg>
)
